import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { formatStorageBytes } from '@/lib/getStorageUsage';

// ── Types ──────────────────────────────────────────────────────────────────────
interface TenantOption {
  id: string;
  name: string;
}

interface FileRow {
  id: string;
  tenant_id: string;
  file_name: string;
  file_type: string | null;
  mime_type: string | null;
  file_size_bytes: number;
  storage_provider: string | null;
  entity_type: string | null;
  file_url: string | null;
  created_at: string;
}

interface Props {
  tenants: TenantOption[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function fileIcon(type: string | null, mime: string | null): string {
  const t = (type ?? mime ?? '').toLowerCase();
  if (t.includes('image') || t.includes('photo') || t.includes('jpg') || t.includes('png') || t.includes('webp'))
    return 'ri-image-line';
  if (t.includes('video') || t.includes('mp4') || t.includes('mov')) return 'ri-video-line';
  if (t.includes('zip') || t.includes('archive')) return 'ri-file-zip-line';
  if (t.includes('pdf')) return 'ri-file-pdf-line';
  return 'ri-file-line';
}

function fileIconColor(type: string | null, mime: string | null): string {
  const t = (type ?? mime ?? '').toLowerCase();
  if (t.includes('image') || t.includes('photo') || t.includes('jpg') || t.includes('png')) return 'text-violet-600 bg-violet-50';
  if (t.includes('video')) return 'text-rose-600 bg-rose-50';
  if (t.includes('zip')) return 'text-amber-600 bg-amber-50';
  if (t.includes('pdf')) return 'text-red-600 bg-red-50';
  return 'text-slate-500 bg-slate-100';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

const PAGE_SIZE = 50;

// ── Component ─────────────────────────────────────────────────────────────────
export default function FilesTab({ tenants }: Props) {
  const [files, setFiles]             = useState<FileRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [page, setPage]               = useState(0);
  const [totalCount, setTotalCount]   = useState(0);

  const tenantMap = Object.fromEntries(tenants.map((t) => [t.id, t.name]));

  const fetchFiles = useCallback(async () => {
    setLoading(true);

    let q = supabase
      .from('uploaded_files')
      .select('id, tenant_id, file_name, file_type, mime_type, file_size_bytes, storage_provider, entity_type, file_url, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (tenantFilter)   q = q.eq('tenant_id', tenantFilter);
    if (providerFilter) q = q.eq('storage_provider', providerFilter);
    if (entityFilter)   q = q.eq('entity_type', entityFilter);
    if (search)         q = q.ilike('file_name', `%${search}%`);

    const { data, count } = await q;
    setFiles((data ?? []) as FileRow[]);
    setTotalCount(count ?? 0);
    setLoading(false);
  }, [page, tenantFilter, providerFilter, entityFilter, search]);

  useEffect(() => {
    setPage(0);
  }, [tenantFilter, providerFilter, entityFilter, search]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Derive unique providers + entity types from loaded files (rough set)
  const providers  = [...new Set(files.map((f) => f.storage_provider).filter(Boolean))] as string[];
  const entities   = [...new Set(files.map((f) => f.entity_type).filter(Boolean))] as string[];

  const totalSize = files.reduce((s, f) => s + (f.file_size_bytes ?? 0), 0);

  return (
    <div className="space-y-5">

      {/* ── Filters ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              placeholder="Buscar por nombre de archivo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
            />
          </div>

          {/* Tenant filter */}
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="w-full py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-slate-700 cursor-pointer"
          >
            <option value="">Todos los tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          {/* Provider filter */}
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="w-full py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-slate-700 cursor-pointer"
          >
            <option value="">Todos los providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Entity type pills */}
        {entities.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
            <button
              onClick={() => setEntityFilter('')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                !entityFilter ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              All types
            </button>
            {entities.map((e) => (
              <button
                key={e}
                onClick={() => setEntityFilter(e === entityFilter ? '' : e)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  entityFilter === e ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <p className="text-slate-700 text-sm font-semibold">
            {loading ? 'Cargando...' : `${totalCount.toLocaleString()} archivos`}
          </p>
          {!loading && files.length > 0 && (
            <p className="text-slate-400 text-xs">
              Esta página: <span className="font-medium text-slate-600">{formatStorageBytes(totalSize)}</span>
            </p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Archivo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Entity</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Tamaño</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <i className="ri-loader-4-line animate-spin text-blue-400 text-2xl"></i>
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">
                    <i className="ri-file-list-line text-2xl mb-2 block text-slate-200"></i>
                    No se encontraron archivos
                  </td>
                </tr>
              ) : (
                files.map((file) => {
                  const iconCls = fileIconColor(file.file_type, file.mime_type);
                  return (
                    <tr key={file.id} className="hover:bg-slate-50/60 transition-colors">
                      {/* File name */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3 max-w-xs">
                          <span className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${iconCls.split(' ')[1]}`}>
                            <i className={`${fileIcon(file.file_type, file.mime_type)} text-sm ${iconCls.split(' ')[0]}`}></i>
                          </span>
                          <div className="min-w-0">
                            <p className="text-slate-700 font-medium text-xs truncate max-w-[160px]" title={file.file_name ?? '—'}>
                              {file.file_name ?? '—'}
                            </p>
                            {file.mime_type && (
                              <p className="text-slate-400 text-[10px] truncate">{file.mime_type}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Tenant */}
                      <td className="px-4 py-3">
                        <span className="text-slate-600 text-xs">
                          {tenantMap[file.tenant_id] ?? <span className="text-slate-300 font-mono text-[10px]">{file.tenant_id.slice(0, 8)}…</span>}
                        </span>
                      </td>

                      {/* Entity */}
                      <td className="px-4 py-3">
                        {file.entity_type ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-semibold uppercase tracking-wide">
                            {file.entity_type}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Size */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-slate-700 text-sm font-semibold">
                          {formatStorageBytes(file.file_size_bytes ?? 0)}
                        </span>
                      </td>

                      {/* Provider */}
                      <td className="px-4 py-3">
                        {file.storage_provider ? (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                            file.storage_provider === 'vps'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : file.storage_provider === 'supabase'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-slate-50 text-slate-500 border-slate-200'
                          }`}>
                            {file.storage_provider}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        <span className="text-slate-400 text-xs">{fmtDate(file.created_at)}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between">
            <p className="text-slate-400 text-xs">
              Página {page + 1} de {totalPages} · {totalCount.toLocaleString()} archivos
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-sm"></i>
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                return (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                      pg === page
                        ? 'bg-blue-600 text-white'
                        : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {pg + 1}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
